import { useContext, useEffect, useRef, useState } from "react";
import {
	Button,
	Form,
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiFilter, BiPlus, BiSearch } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";
import { CompanyInfoContext } from "../../../../App";
import { LS_DCE_FILTER_KEY, startYear } from "../../../../core/constants/common";
import {
	formatPeriodRangeForYear,
	getCompanyInfoFromStorage,
	getDefaultSelectableYear,
	getMaxSelectableYear,
	isFinancialYearReporting,
} from "../../../../core/utilities/assessmentPeriodUtils";
import useHasPermission from "../../../../core/hooks/useHasPermission";
import "../../../SubscriberSupplierAdmin/CompanySettings/QualitativeFilterStatus.scss";
import "../Forms.scss";
import { create_dce } from "../CreateDataCollectionEvent/CreateDataCollectionEvent";
import QualitativeDCEModal from "../NewAssessmentModal/QualitativeDCEModal";
import DCEFilterPanel from "./DCEFilterPanel";
import QualitativeDCEList from "./QualitativeDCEList";
import QuantitativeDCEList from "./QuantitativeDCEList";

const buildDefaultStatusFilters = () => ({
	NOT_STARTED: false,
	IN_PROGRESS: true,
	COMPLETED: false,
	EXPIRED: false
});

const buildDefaultFilters = (companyInfo) => ({
	submissionStatus: "IN_PROGRESS",
	year: getDefaultSelectableYear(companyInfo),
	statusFilters: buildDefaultStatusFilters()
});

function DCEListContainer({ t }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { companyInfo: contextCompanyInfo } = useContext(CompanyInfoContext);
	const [showModal, setShowModal] = useState(location?.state?.startNew ? true : false);
	const [activeView, setActiveView] = useState(location?.state?.isQualitative ? 'QUAL' : 'QUANT');
	const [searchKey, setSearchKey] = useState('');
	const [updatedOn, setUpdatedOn] = useState(0);
	const [showFilter, setShowFilter] = useState(false);
	const yearUserSelectedRef = useRef(false);

	const resolveCompanyInfo = () => {
		if (contextCompanyInfo && typeof contextCompanyInfo === 'object') {
			return contextCompanyInfo;
		}
		return getCompanyInfoFromStorage();
	};

	const getDCEFilterFromStorage = () => {
		const savedFilter = localStorage.getItem(LS_DCE_FILTER_KEY);
		return savedFilter ? JSON.parse(savedFilter) : null;
	};

	const saveDCEFilterToStorage = (filterData, yearUserSelected = yearUserSelectedRef.current) => {
		yearUserSelectedRef.current = yearUserSelected;
		localStorage.setItem(LS_DCE_FILTER_KEY, JSON.stringify({
			filters: filterData,
			yearUserSelected
		}));
	};

	const resolveFilterYear = (savedYear, companyInfo, yearUserSelected) => {
		const defaultYear = getDefaultSelectableYear(companyInfo);
		const maxYear = getMaxSelectableYear();
		const parsedYear = Number(savedYear);
		const calendarYear = new Date().getFullYear();

		if (!Number.isFinite(parsedYear) || parsedYear < startYear || parsedYear > maxYear) {
			return defaultYear;
		}

		if (
			!yearUserSelected &&
			isFinancialYearReporting(companyInfo) &&
			parsedYear === calendarYear &&
			defaultYear === calendarYear + 1
		) {
			return defaultYear;
		}

		return parsedYear;
	};

	const resolveFiltersFromStorage = (companyInfo) => {
		const savedData = getDCEFilterFromStorage();
		const defaultFilters = buildDefaultFilters(companyInfo);
		const yearUserSelected = Boolean(savedData?.yearUserSelected);

		if (!savedData?.filters) {
			return { filters: defaultFilters, yearUserSelected: false };
		}

		return {
			filters: {
				...defaultFilters,
				...savedData.filters,
				year: resolveFilterYear(savedData.filters.year, companyInfo, yearUserSelected),
				statusFilters: savedData.filters.statusFilters ?? defaultFilters.statusFilters
			},
			yearUserSelected
		};
	};

	const [filters, setFilters] = useState(() => resolveFiltersFromStorage(getCompanyInfoFromStorage()).filters);
	const { hasDataCollectionUpdatePermission } = useHasPermission();

	const syncFiltersFromCompanyInfo = () => {
		const companyInfo = resolveCompanyInfo();
		const { filters: resolvedFilters, yearUserSelected } = resolveFiltersFromStorage(companyInfo);
		yearUserSelectedRef.current = yearUserSelected;
		setFilters(resolvedFilters);
		saveDCEFilterToStorage(resolvedFilters, yearUserSelected);
		setUpdatedOn(Date.now());
	};

	// Re-resolve default year once company FY settings are available (Header loads async)
	useEffect(() => {
		syncFiltersFromCompanyInfo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contextCompanyInfo?.dataFrequencyType,
		contextCompanyInfo?.frequencyType,
		contextCompanyInfo?.financialYearStart
	]);

	const handleSearch = (key) => {
		setSearchKey(key);
	};

	const onNewButtonClick = () => {
		navigate(create_dce);
	};

	const headerToRender = () => {
		return (
			<div className="List_header">
				<h5 className="MH">{t("DataCollectionEvents.label")}</h5>
				<div className="d-flex justify-content-end align-items-center header-actions">
					<div className="mx-2" hidden>
						<Form.Check
							inline
							className="toggle-switch"

							label={t("Quantitative.label")}
							name="assessType"
							type="radio"
							id="inline-radio-2"
							value={'Quantitative'}
							checked={activeView === 'QUANT' ? "checked" : ''}
							onChange={() => setActiveView('QUANT')}
						/>
						<Form.Check
							inline
							className="toggle-switch"
							label={t("Qualitative.label")}
							name="assessType"
							type="radio"
							id="inline-radio-1"
							value={'Qualitative'}
							checked={activeView === 'QUAL' ? "checked" : ''}
							onChange={() => setActiveView('QUAL')}
						/>
					</div>
					{renderActiveFilters()}
					<button
						className="btn btn-headbtn filter-icon"
						onClick={() => setShowFilter(!showFilter)}
					>
						<BiFilter />
					</button>
					<Button variant="primary" hidden={!hasDataCollectionUpdatePermission()} onClick={() => {
						onNewButtonClick()
					}}>

						<BiPlus className="me-1" />
						{t("New.label")}
					</Button>

				</div>
			</div>
		);
	};

	const headerActionsToRender = () => {
		return (
			<div className="List_table-card01_head">
				<div className="List_table-card_Search_01">
					<div className="List_table-card_Search_Box">
						<Form>
							<input
								type="text"
								className="form-control"
								placeholder={t("SearchByName.label")}
								value={searchKey}
								onChange={(e) => handleSearch(e.target.value)}
							/>
							<a className="Search_iocnBox cursor"
								onClick={() => handleSearch(searchKey)}>
								<BiSearch />
							</a>
						</Form>
					</div>
				</div>
			</div>
		);
	};

	const handleApplyFilter = (filterData) => {
		const newFilters = {
			submissionStatus: filterData.submissionStatus,
			year: filterData.year != null && filterData.year !== ''
				? Number(filterData.year)
				: null,
			statusFilters: filterData.statusFilters || {
				NOT_STARTED: false,
				IN_PROGRESS: false,
				COMPLETED: false,
				EXPIRED: false
			}
		};
		setFilters(newFilters);
		saveDCEFilterToStorage(newFilters, true);
		setUpdatedOn(Date.now());
		setShowFilter(false);
	};

	const handleResetFilter = () => {
		const defaultFilters = buildDefaultFilters(resolveCompanyInfo());
		setFilters(defaultFilters);
		saveDCEFilterToStorage(defaultFilters, false);
		setUpdatedOn(Date.now());
	};


	const renderActiveFilters = () => {
		const activeFilters = [];
		const statusLabels = {
			NOT_STARTED: t("NotStarted.label"),
			IN_PROGRESS: t("InProgress.label"),
			COMPLETED: t("Completed.label"),
			EXPIRED: t("Expired.label")
		};

		if (filters.year) {
			const companyInfo = resolveCompanyInfo();
			const periodRangeText = formatPeriodRangeForYear(filters.year, companyInfo, t);
			activeFilters.push(
				<span
					key="year-period-range"
					className="qualitative-period-range"
					data-testid="dce-year-period-range"
				>
					<span className="qualitative-period-range__label">{t("Period.label")}:</span>
					<span className="qualitative-period-range__value">{periodRangeText}</span>
				</span>
				
			);
		}

		// Combine all selected statuses into a single tag
		const selectedStatuses = Object.entries(filters.statusFilters || {})
			.filter(([_, isSelected]) => isSelected)
			.map(([status]) => statusLabels[status]);

		if (selectedStatuses.length > 0) {
			activeFilters.push(
				<span key="status-filter" className="Filter_status">
					<h6>{t("Status.label")}: </h6>
					{selectedStatuses.join(", ")}
				</span>
			);
		}

		return activeFilters.length > 0 ? (
			<div className="qualitative-filter-status">
				{activeFilters}
			</div>
		) : null;
	};

	return (
		<>
			<div className="FormsList">
				{headerToRender()}
				<div className="List_Table_01 mt-2">
					<div className="row m-0">
						<div className={showFilter ? "col-lg-12 col-md-12" : "col-12"}>
							{headerActionsToRender()}
							{
								activeView === 'QUANT' &&
								<QuantitativeDCEList
									searchKey={searchKey}
									updatedOn={updatedOn}
									filters={filters}
								/>
							}
							{
								activeView === 'QUAL' &&
								<QualitativeDCEList searchKey={searchKey} updatedOn={updatedOn} />
							}
						</div>
						{showFilter && (
							<DCEFilterPanel
								onApplyFilter={handleApplyFilter}
								onResetFilter={handleResetFilter}
								onFilterHide={setShowFilter}
								initialFilters={filters}
							/>
						)}
					</div>
					{showModal && activeView === 'QUAL' &&
						<QualitativeDCEModal
							onHide={() => {
								setShowModal(false);
							}}
							onSave={() => setUpdatedOn(Date.now())}
						/>
					}
				</div>
			</div >
		</>
	);
}
DCEListContainer.propTypes = {};

export default withTranslation()(DCEListContainer);
